import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export default function PermissionsDialog({ user, open, onOpenChange, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: modules = [] } = useQuery({
    queryKey: ["auth-modules"],
    queryFn: async () => (await api.get("/auth/modules")).data,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (user) setSelected(user.permissions || []);
  }, [user, open]);

  if (!user) return null;
  const isAdmin = user.role === "admin";

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const setAll = (val) => {
    if (val) {
      setSelected(modules.filter((m) => isAdmin || m.key !== "users").map((m) => m.key));
    } else {
      setSelected([]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { permissions: selected });
      toast.success("Permisos actualizados");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar permisos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" /> Permisos de @{user.username}
          </DialogTitle>
          <DialogDescription>
            Selecciona las secciones y acciones que puede usar este usuario.
            {isAdmin && (
              <span className="block mt-1 text-amber-400">
                Los administradores siempre tienen acceso a todo (no se puede limitar).
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
          <div className="text-xs text-muted-foreground">
            {selected.length} de {modules.length} seleccionados
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setAll(true)} disabled={isAdmin} data-testid="perms-select-all">
              Seleccionar todo
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAll(false)} disabled={isAdmin} data-testid="perms-clear-all">
              Limpiar
            </Button>
          </div>
        </div>

        <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
          {modules.map((m) => {
            const checked = isAdmin ? true : selected.includes(m.key);
            const isUsers = m.key === "users";
            const disabled = isAdmin || (isUsers && user.role !== "admin");
            return (
              <label
                key={m.key}
                htmlFor={`perm-${m.key}`}
                className={`flex items-center gap-3 p-2.5 rounded-md border border-border bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`perms-row-${m.key}`}
              >
                <Checkbox
                  id={`perm-${m.key}`}
                  checked={checked}
                  onCheckedChange={() => !disabled && toggle(m.key)}
                  disabled={disabled}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{m.key}</div>
                </div>
                {isUsers && user.role !== "admin" && (
                  <Badge variant="outline" className="text-[9px] uppercase">Solo admins</Badge>
                )}
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving || isAdmin} data-testid="perms-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Guardar permisos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
